/**
 * Date utilities shared across care-time hooks and components.
 *
 * Centralizes month-boundary computation so the widget summary hook,
 * the activity-log hook, and the registry hook (Phase 2) all agree
 * on what "this month" means for a given JS Date.
 *
 * @agentic connections
 */

/** Pair of ISO 8601 strings anchoring a calendar month. @agentic connections */
export type CalendarMonthBounds = {
  start: string;
  end: string;
};

/**
 * Compute the first/last day of the calendar month containing `date`,
 * as ISO 8601 strings. Uses UTC boundaries for stability across
 * timezones — the underlying FHIR search treats `date` as a calendar
 * comparison.
 * @agentic connections
 */
export const getCalendarMonthBounds = (date: Date): CalendarMonthBounds => {
  const start = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 0, 0, 0, 0),
  );
  const end = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0, 23, 59, 59, 999),
  );
  return { start: start.toISOString(), end: end.toISOString() };
};

/**
 * UTC timestamp for the first instant of the calendar month containing
 * `date`. Used to compare months without being sensitive to the day or
 * time of day within a month.
 * @agentic connections
 */
const monthStartMs = (date: Date): number =>
  Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1);

/**
 * Whether the calendar month of `candidate` is strictly after the
 * calendar month of `reference` (defaults to now). The registry reports
 * on billing months that have already begun, so navigating into a future
 * month must be blocked.
 * @agentic connections
 */
export const isFutureMonth = (
  candidate: Date,
  reference: Date = new Date(),
): boolean => monthStartMs(candidate) > monthStartMs(reference);

/**
 * The calendar month immediately after the month containing `date`,
 * anchored at the first instant (00:00:00 UTC) of that next month.
 * @agentic connections
 */
export const getNextMonth = (date: Date): Date =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1));

/**
 * The calendar month immediately before the month containing `date`,
 * anchored at the first instant (00:00:00 UTC) of that previous month.
 * @agentic connections
 */
export const getPreviousMonth = (date: Date): Date =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() - 1, 1));
